import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { PillButton, api, Button, Card, ConfirmDialog, Field, apiErrorMessage, brandDisplayName, brandLocalDate, rebaseDatePrefill, useBrand } from '@aralere/shared-ui';
import type {
  Business,
  CreateRecurringExpenseRequest,
  RecurringExpenseView,
  RecurringFrequency,
  Service,
  Vendor,
} from '@aralere/shared-types';

// Expenses admin (spec §10.2; s43 record §1.1–1.2 + the slice-2 derived license model — 08
// seat-drift resolution). Licenses store per-seat INPUTS (price per seat, block size, optional
// floor); purchased / amount / available all DERIVE live from actual in-use, so the expense can
// never drift from reality — the form has NO amount field for a license (it shows the derived
// figure instead). Subscriptions stay flat per-cycle amounts; a flat amount edit appends an
// amountHistory step, a per-seat price edit appends a pricePerSeatHistory step (derive-on-read —
// past months never rewrite). The edit form surfaces service⇄expense COST DRIFT (price per seat vs
// the catalog's Cost to you) with explicit one-click reconcile — never silent (cost feeds margin).
// Delete is a TRUE delete behind a strong confirm (owner decision, s44). The general one-time
// expenses surface (spec §10.6) joins this page at a later Stage-3 slice.

interface FormState {
  businessId: string;
  type: 'license' | 'subscription';
  description: string;
  linkedServiceId: string;
  linkedVendorId: string;
  amount: string;
  pricePerSeat: string;
  seatsPerBlock: string;
  seatsFloor: string;
  externalSeats: string;
  frequency: RecurringFrequency;
  frequencyMonths: string;
  chargeDay: string;
  startDate: string;
  endDate: string;
  amountEffectiveFrom: string;
  pricePerSeatEffectiveFrom: string;
}

// 04 #136: the start date prefills to today on the brand clock (02 s177): the portal brand's until a
// business is chosen, then that brand's (withBrand below).
const emptyForm = (timezone: string | null): FormState => ({
  businessId: '',
  type: 'license',
  description: '',
  linkedServiceId: '',
  linkedVendorId: '',
  amount: '',
  pricePerSeat: '',
  seatsPerBlock: '1',
  seatsFloor: '',
  externalSeats: '',
  frequency: 'monthly',
  frequencyMonths: '',
  chargeDay: '1',
  startDate: brandLocalDate(timezone),
  endDate: '',
  amountEffectiveFrom: '',
  pricePerSeatEffectiveFrom: '',
});

const formFromEntry = (v: RecurringExpenseView): FormState => ({
  businessId: v.expense.businessId,
  type: v.expense.type,
  description: v.expense.description,
  linkedServiceId: v.expense.linkedServiceId ?? '',
  linkedVendorId: v.expense.linkedVendorId ?? '',
  amount: String(v.expense.amount),
  // A LEGACY license (no pricePerSeat yet) opens with these blank — saving requires filling them,
  // which moves the row onto the derived seat engine (server-enforced; test-data-only rows).
  pricePerSeat: v.expense.pricePerSeat != null ? String(v.expense.pricePerSeat) : '',
  seatsPerBlock: v.expense.seatsPerBlock != null ? String(v.expense.seatsPerBlock) : '1',
  seatsFloor: v.expense.seatsFloor != null ? String(v.expense.seatsFloor) : '',
  externalSeats: v.expense.externalSeats != null ? String(v.expense.externalSeats) : '',
  frequency: v.expense.frequency,
  frequencyMonths: v.expense.frequencyMonths != null ? String(v.expense.frequencyMonths) : '',
  chargeDay: String(v.expense.chargeDay),
  startDate: v.expense.startDate,
  endDate: v.expense.endDate ?? '',
  amountEffectiveFrom: '',
  pricePerSeatEffectiveFrom: '',
});

const FREQ_LABEL: Record<RecurringFrequency, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
  custom: 'Every N months',
};

export function RecurringExpensesPanel(): JSX.Element {
  const [entries, setEntries] = useState<RecurringExpenseView[] | null>(null);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const { brand: portalBrand } = useBrand();
  const [form, setForm] = useState<FormState>(() => emptyForm(portalBrand?.timezone ?? null));
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<RecurringExpenseView | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [attachOpen, setAttachOpen] = useState<Set<string>>(new Set());
  const [attachBusy, setAttachBusy] = useState<string | null>(null);
  const [attachDeletingRec, setAttachDeletingRec] = useState<string | null>(null);
  const recAttachInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = async (): Promise<void> => {
    try {
      const res = await api.admin.listRecurringExpenses();
      setEntries(res.entries);
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  };

  const toggleAttach = (id: string): void =>
    setAttachOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const onUploadRec = async (id: string, file: File | null): Promise<void> => {
    if (!file) return;
    setAttachBusy(id);
    setAttachDeletingRec(null);
    try {
      await api.admin.addRecurringAttachment(id, file);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAttachBusy(null);
    }
  };
  const onRemoveRec = async (id: string, attachmentId: string): Promise<void> => {
    setAttachBusy(id);
    setAttachDeletingRec(attachmentId);
    try {
      await api.admin.deleteRecurringAttachment(id, attachmentId);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setAttachBusy(null);
      setAttachDeletingRec(null);
    }
  };
  const renderRecurringAttachments = (v: RecurringExpenseView): JSX.Element => {
    const id = v.expense.id;
    const atts = v.expense.attachments ?? [];
    const busy = attachBusy === id;
    const uploading = busy && attachDeletingRec === null;
    return (
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium">Attachments <span className="text-[var(--color-muted)]">(receipts, invoices)</span></span>
          <input
            ref={(el) => { recAttachInputs.current[id] = el; }}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            className="hidden"
            onChange={(ev) => {
              const f = ev.target.files?.[0] ?? null;
              ev.target.value = '';
              void onUploadRec(id, f);
            }}
          />
          <PillButton disabled={busy} onClick={() => recAttachInputs.current[id]?.click()}>{uploading ? 'Uploading...' : 'Add file'}</PillButton>
        </div>
        {atts.length === 0 ? (
          <p className="text-xs text-[var(--color-muted)]">No files attached.</p>
        ) : (
          <ul className="space-y-1">
            {atts.map((a) => (
              <li key={a.attachmentId} className="flex items-center justify-between gap-2 text-sm">
                <a href={api.admin.recurringAttachmentUrl(id, a.attachmentId)} target="_blank" rel="noopener noreferrer" className="truncate text-[var(--link-text)] hover:underline">{a.filename}</a>
                <button type="button" disabled={busy} className="shrink-0 text-xs font-medium text-[var(--tint-red-text)] hover:underline disabled:opacity-60" onClick={() => void onRemoveRec(id, a.attachmentId)}>{attachDeletingRec === a.attachmentId ? 'Deleting...' : 'Remove'}</button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  useEffect(() => {
    void load();
    void (async () => {
      try {
        const [biz, svc, ven] = await Promise.all([
          api.admin.listBusinesses(),
          api.admin.listServices(),
          api.admin.listVendors(),
        ]);
        setBusinesses(biz.businesses);
        setServices(svc.services);
        setVendors(ven.vendors.filter((v) => v.active));
      } catch (e) {
        setError(apiErrorMessage(e));
      }
    })();
  }, []);

  const update = (patch: Partial<FormState>): void => setForm((f) => ({ ...f, ...patch }));

  /** 04 #136: a business's timezone (its `timezone`, absent = the platform default); with no business
   *  chosen, the portal brand's. */
  const bizTz = (id: string): string | null => {
    const b = businesses.find((x) => x.id === id);
    return b ? b.timezone ?? null : portalBrand?.timezone ?? null;
  };
  /** 04 #136: set the business; on a NEW expense an untouched start-date prefill follows the new
   *  brand's clock (an edit keeps its stored date). */
  const withBrand = (f: FormState, businessId: string): FormState =>
    editingId ? { ...f, businessId } : { ...f, businessId, startDate: rebaseDatePrefill(f.startDate, bizTz(f.businessId), bizTz(businessId)) };

  // The businessId defaults from the linked service's defaultBillingEntity (s43 §1.1) — editable.
  const pickService = (serviceId: string): void => {
    const svc = services.find((s) => s.id === serviceId);
    setForm((f) => ({
      ...withBrand(f, f.businessId === '' && svc ? svc.defaultBillingEntity : f.businessId),
      linkedServiceId: serviceId,
    }));
  };

  const openCreate = (): void => {
    setEditingId(null);
    setForm(emptyForm(portalBrand?.timezone ?? null));
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (v: RecurringExpenseView): void => {
    setEditingId(v.expense.id);
    setForm(formFromEntry(v));
    setFormError(null);
    setFormOpen(true);
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  const buildPayload = (): CreateRecurringExpenseRequest | null => {
    if (form.businessId === '') { setFormError('Pick the business this expense belongs to.'); return null; }
    if (form.description.trim() === '') { setFormError('A description is required.'); return null; }
    const isLicense = form.type === 'license';
    let amount: number | null = null;
    if (!isLicense) {
      amount = Number(form.amount.trim());
      if (!(amount >= 0)) { setFormError('Amount must be a number ≥ 0.'); return null; }
    }
    let pricePerSeat: number | null = null;
    let seatsPerBlock: number | null = null;
    let seatsFloor: number | null = null;
    let externalSeats: number | null = null;
    if (isLicense) {
      pricePerSeat = Number(form.pricePerSeat.trim());
      if (form.pricePerSeat.trim() === '' || !(pricePerSeat >= 0)) {
        setFormError('A license needs a price per seat (a number ≥ 0). A flat cost with no seats should be a subscription instead.');
        return null;
      }
      seatsPerBlock = Number(form.seatsPerBlock.trim() === '' ? '1' : form.seatsPerBlock.trim());
      if (!Number.isInteger(seatsPerBlock) || seatsPerBlock < 1) {
        setFormError('Seats per block must be a whole number ≥ 1 (1 = per-seat pricing).');
        return null;
      }
      if (form.seatsFloor.trim() !== '') {
        seatsFloor = Number(form.seatsFloor.trim());
        if (!Number.isInteger(seatsFloor) || seatsFloor < 0) {
          setFormError('The prepaid floor must be a whole number of seats ≥ 0 (or blank).');
          return null;
        }
      }
      if (form.externalSeats.trim() !== '') {
        externalSeats = Number(form.externalSeats.trim());
        if (!Number.isInteger(externalSeats) || externalSeats < 0) {
          setFormError('Seats outside the portal must be a whole number ≥ 0 (or blank).');
          return null;
        }
      }
    }
    const chargeDay = Number(form.chargeDay.trim());
    if (!Number.isInteger(chargeDay) || chargeDay < 1 || chargeDay > 31) {
      setFormError('Charge day must be a whole number from 1 to 31.');
      return null;
    }
    if (form.startDate.trim() === '') { setFormError('A start date is required.'); return null; }
    let frequencyMonths: number | null = null;
    if (form.frequency === 'custom') {
      frequencyMonths = Number(form.frequencyMonths.trim());
      if (!Number.isInteger(frequencyMonths) || frequencyMonths < 1 || frequencyMonths > 120) {
        setFormError('Every-N-months must be a whole number from 1 to 120.');
        return null;
      }
    }
    return {
      businessId: form.businessId,
      type: form.type,
      description: form.description.trim(),
      linkedServiceId: form.linkedServiceId || null,
      linkedVendorId: form.linkedVendorId || null,
      // A license's amount DERIVES (seats × price per seat) — never sent (the server rejects it).
      ...(isLicense ? {} : { amount }),
      pricePerSeat,
      seatsPerBlock: isLicense ? seatsPerBlock : null,
      seatsFloor,
      externalSeats,
      frequency: form.frequency,
      frequencyMonths,
      chargeDay,
      startDate: form.startDate,
      endDate: form.endDate.trim() === '' ? null : form.endDate,
    };
  };

  const submit = async (): Promise<void> => {
    const payload = buildPayload();
    if (!payload) return;
    setSubmitting(true);
    setFormError(null);
    try {
      if (editingId) {
        await api.admin.updateRecurringExpense(editingId, {
          ...payload,
          ...(form.amountEffectiveFrom.trim() !== '' ? { amountEffectiveFrom: form.amountEffectiveFrom } : {}),
          ...(form.pricePerSeatEffectiveFrom.trim() !== '' ? { pricePerSeatEffectiveFrom: form.pricePerSeatEffectiveFrom } : {}),
        });
      } else {
        await api.admin.createRecurringExpense(payload);
      }
      await load();
      closeForm();
    } catch (err) {
      setFormError(apiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    setError(null);
    try {
      await api.admin.deleteRecurringExpense(deleteTarget.expense.id);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  const money = (n: number): string => `$${n.toFixed(2)}`;

  // Normalize every recurring expense to a monthly figure and total it (overall + per brand). A
  // license amount is the server's derived per-cycle snapshot; quarterly/annual/custom divide down.
  const recurringTotals = useMemo(() => {
    if (!entries) return null;
    const monthlyOf = (v: RecurringExpenseView): number => {
      const amt = v.expense.amount ?? 0;
      switch (v.expense.frequency) {
        case 'monthly': return amt;
        case 'quarterly': return amt / 3;
        case 'annual': return amt / 12;
        case 'custom': return v.expense.frequencyMonths ? amt / v.expense.frequencyMonths : 0;
        default: return 0;
      }
    };
    let overall = 0;
    const byBrand = new Map<string, { name: string; monthly: number }>();
    for (const v of entries) {
      const m = monthlyOf(v);
      overall += m;
      const key = v.expense.businessId;
      const row = byBrand.get(key) ?? { name: v.business?.name ?? key, monthly: 0 };
      row.monthly += m;
      byBrand.set(key, row);
    }
    return { overall, byBrand: [...byBrand.values()].sort((a, b) => b.monthly - a.monthly) };
  }, [entries]);
  // A service can hold at most ONE current per-seat license (the server 409s a second — two would
  // double-derive purchased). A subscription MAY coexist with a license on the same service (owner
  // decision, s45), so the picker only hides a service when the CURRENT form is a license and that
  // service already has a seat-based license on another expense. Everything else stays selectable,
  // and the row being edited always keeps its own service so an edit can leave it in place.
  // 04 #136: "current" is judged on each expense's own brand clock (the server's seat-utilization
  // notEnded shape), never the UTC day.
  const seatLicensedElsewhere = new Set(
    (entries ?? [])
      .filter(
        (e) =>
          e.expense.id !== editingId &&
          e.expense.type === 'license' &&
          e.expense.pricePerSeat != null &&
          e.expense.linkedServiceId != null &&
          (e.expense.endDate == null || e.expense.endDate >= brandLocalDate(bizTz(e.expense.businessId))),
      )
      .map((e) => e.expense.linkedServiceId as string),
  );
  const linkableServices =
    form.type === 'license'
      ? services.filter((s) => !seatLicensedElsewhere.has(s.id) || s.id === form.linkedServiceId)
      : services;
  const cycleLabel = (v: RecurringExpenseView): string =>
    v.expense.frequency === 'custom'
      ? `every ${v.expense.frequencyMonths ?? '?'} mo`
      : FREQ_LABEL[v.expense.frequency].toLowerCase();

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <p className="max-w-3xl text-xs text-[var(--color-muted)]">
          This is the <strong>cost side</strong>: what <em>you</em> pay vendors for licenses and
          subscriptions. These count toward profitability even when unassigned. How you <em>bill a
          client</em> for a service, and what happens to their access on cancel, is the other side
          of the ledger; it lives on each service's <em>Lifecycle &amp; billing term</em> section
          in the Services catalog.
        </p>
        <Button type="button" onClick={openCreate}>New recurring expense</Button>
      </div>

      {recurringTotals ? (
        <Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg">Recurring totals</h2>
              <p className="text-xs text-[var(--color-muted)]">All recurring expenses, normalized to each period.</p>
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <div><span className="text-[var(--color-muted)]">Monthly </span><span className="font-medium">{money(recurringTotals.overall)}</span></div>
              <div><span className="text-[var(--color-muted)]">Quarterly </span><span className="font-medium">{money(recurringTotals.overall * 3)}</span></div>
              <div><span className="text-[var(--color-muted)]">Yearly </span><span className="font-medium">{money(recurringTotals.overall * 12)}</span></div>
            </div>
          </div>
          {recurringTotals.byBrand.length > 1 ? (
            <div className="mt-3 space-y-1 border-t border-[rgb(var(--edge-rgb)/0.05)] pt-3">
              {recurringTotals.byBrand.map((b) => (
                <div key={b.name} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-[var(--color-muted)]">{brandDisplayName(b.name)}</span>
                  <span>{money(b.monthly)}/mo · {money(b.monthly * 3)}/qtr · {money(b.monthly * 12)}/yr</span>
                </div>
              ))}
            </div>
          ) : null}
        </Card>
      ) : null}

      {error ? <p className="text-sm text-[var(--tint-red-text)]">{error}</p> : null}

      {formOpen ? (
        <Card>
          <div className="space-y-4">
            <p className="text-lg font-medium">{editingId ? 'Edit recurring expense' : 'New recurring expense'}</p>
            <div className="grid gap-4 md:grid-cols-3">
              <Field
                label="Type"
                required
                tip="License: seat-tracked and linked to a catalog service. You set the price per seat and how seats are sold (per-seat or in blocks); the amount then derives automatically from seats actually in use. Subscription: a flat recurring business cost with no seats, like an ISP bill or accounting software."
              >
                <Select value={form.type} onChange={(v) => update({ type: v as FormState['type'] })}>
                  <option value="license">License</option>
                  <option value="subscription">Subscription</option>
                </Select>
              </Field>
              <Field label="Description" required>
                <Input value={form.description} onChange={(v) => update({ description: v })} placeholder="Microsoft 365 Business Premium seats" />
              </Field>
              <Field label="Business" required>
                <Select value={form.businessId} onChange={(v) => setForm((f) => withBrand(f, v))}>
                  <option value="">Select…</option>
                  {businesses.map((b) => (
                    <option key={b.id} value={b.id}>{brandDisplayName(b.name)}</option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Linked service (optional)"
                tip="A service can have one per-seat license at a time, so when you're adding a license, any service that already has one is hidden here. A subscription and a license can share a service."
              >
                <Select value={form.linkedServiceId} onChange={pickService}>
                  <option value="">None</option>
                  {linkableServices.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Linked vendor (optional)">
                <Select value={form.linkedVendorId} onChange={(v) => update({ linkedVendorId: v })}>
                  <option value="">None</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.displayName}</option>
                  ))}
                </Select>
              </Field>
              {form.type === 'subscription' ? (
                <Field label="Amount (per cycle)" required>
                  <Input value={form.amount} onChange={(v) => update({ amount: v })} placeholder="22.00" />
                </Field>
              ) : (
                <>
                  <Field
                    label="Price per seat"
                    required
                    tip="What one seat costs you per cycle. The amount per cycle derives from this automatically: seats in use, rounded up to your purchase block, times this price, so the expense can never disagree with actual usage."
                  >
                    <Input value={form.pricePerSeat} onChange={(v) => update({ pricePerSeat: v })} placeholder="16.40" />
                  </Field>
                  <Field
                    label="Seats per block"
                    required
                    tip="How the vendor sells seats. 1 means one at a time (per-seat, like CSP). 10 means packs of ten, so going from 10 to 11 in use derives 20 purchased."
                  >
                    <Input value={form.seatsPerBlock} onChange={(v) => update({ seatsPerBlock: v })} placeholder="1" />
                  </Field>
                  <Field
                    label="Prepaid floor (seats, optional)"
                    tip="A minimum you're committed to paying for even when unused, in seats. The derived purchased count never drops below it. Leave blank for none."
                  >
                    <Input value={form.seatsFloor} onChange={(v) => update({ seatsFloor: v })} placeholder="none" />
                  </Field>
                  <Field
                    label="Extra seats NOT assigned in the portal (optional)"
                    tip="Leave this 0 in almost every case. It is ONLY for seats you pay for that are NOT assigned to any client in the portal, e.g. licenses you deployed by hand before you started using the portal. Every service you assign in the portal is already counted automatically; do not re-enter those here or they'll be counted twice."
                  >
                    <Input value={form.externalSeats} onChange={(v) => update({ externalSeats: v })} placeholder="0" />
                  </Field>
                </>
              )}
              <Field label="Frequency" required>
                <Select value={form.frequency} onChange={(v) => update({ frequency: v as RecurringFrequency })}>
                  {(Object.keys(FREQ_LABEL) as RecurringFrequency[]).map((f) => (
                    <option key={f} value={f}>{FREQ_LABEL[f]}</option>
                  ))}
                </Select>
              </Field>
              {form.frequency === 'custom' ? (
                <Field label="Every N months" required>
                  <Input value={form.frequencyMonths} onChange={(v) => update({ frequencyMonths: v })} placeholder="6" />
                </Field>
              ) : null}
              <Field label="Charge day (of month)" required>
                <Input value={form.chargeDay} onChange={(v) => update({ chargeDay: v })} placeholder="5" />
              </Field>
              <Field label="Start date" required>
                <DateInput value={form.startDate} onChange={(v) => update({ startDate: v })} />
              </Field>
              <Field label="End date (blank = ongoing)">
                <DateInput value={form.endDate} onChange={(v) => update({ endDate: v })} />
              </Field>
              {editingId && form.type === 'subscription' ? (
                <Field label="Amount change effective from (blank = today)">
                  <DateInput value={form.amountEffectiveFrom} onChange={(v) => update({ amountEffectiveFrom: v })} />
                </Field>
              ) : null}
              {editingId && form.type === 'license' ? (
                <Field label="Price change effective from (blank = today)" tip="Only used when you change the price per seat: the new price applies from this date; past months keep deriving at the old one.">
                  <DateInput value={form.pricePerSeatEffectiveFrom} onChange={(v) => update({ pricePerSeatEffectiveFrom: v })} />
                </Field>
              ) : null}
            </div>
            {form.type === 'license' ? (
              <DerivedAmountNote form={form} view={editingId ? entries?.find((e) => e.expense.id === editingId) ?? null : null} />
            ) : null}
            {editingId ? <CostDriftPanel form={form} view={entries?.find((e) => e.expense.id === editingId) ?? null} onUsePrice={(p) => update({ pricePerSeat: p })} onPushed={() => void load()} /> : null}
            {editingId ? (
              <p className="text-xs text-[var(--color-muted)]">
                {form.type === 'license'
                  ? 'Changing the price per seat records a new price step from its effective date; past months keep deriving at their then-current price (nothing is rewritten).'
                  : 'Changing the amount records a new price step from its effective date; past months keep their old amount (nothing is rewritten).'}
              </p>
            ) : null}
            {formError ? <p className="text-sm text-[var(--tint-red-text)]">{formError}</p> : null}
            <div className="flex gap-2">
              <Button type="button" onClick={() => void submit()} disabled={submitting}>
                {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create expense'}
              </Button>
              <Button type="button" variant="ghost" onClick={closeForm} disabled={submitting}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      <Card>
        {entries == null ? (
          <p className="text-sm text-[var(--color-muted)]">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-[var(--color-muted)]">
            No recurring expenses yet. Add your licenses and business subscriptions so profitability
            counts them, even when unassigned.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--color-muted)]">
                <th className="px-3 py-2 font-medium">Expense</th>
                <th className="px-3 py-2 font-medium">Linked to</th>
                <th className="px-3 py-2 font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Seats</th>
                <th className="px-3 py-2 font-medium">Schedule</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((v) => {
                const over = v.utilization != null && v.utilization.inUse > v.utilization.purchased;
                return (
                  <Fragment key={v.expense.id}>
                  <tr className="border-t border-[rgb(var(--edge-rgb)/0.05)]">
                    <td className="px-3 py-3">
                      <div className="font-medium">{v.expense.description}</div>
                      <div className="text-xs text-[var(--color-muted)]">
                        {v.expense.type === 'license' ? 'License' : 'Subscription'}
                        {v.business ? ` · ${brandDisplayName(v.business.name)}` : ''}
                      </div>
                      {v.expense.type === 'license' &&
                      v.service &&
                      v.expense.pricePerSeat != null &&
                      Math.abs(v.service.defaultCostToYou - v.expense.pricePerSeat) >= 0.005 ? (
                        <span className="mt-1 inline-flex items-center rounded-full bg-[var(--tint-warn-bg)] px-2 py-0.5 text-xs font-medium text-[var(--tint-warn-text)]">
                          Cost drift · edit to reconcile
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-[var(--color-muted)]">
                      <div>{v.service?.name ?? '-'}</div>
                      <div className="text-xs">{v.vendor?.displayName ?? ''}</div>
                    </td>
                    <td className="px-3 py-3">
                      {v.utilization?.basis === 'derived' && v.utilization.amountPerCycle != null ? (
                        <>
                          <div>{money(v.utilization.amountPerCycle)}</div>
                          <div className="text-xs text-[var(--color-muted)]">
                            {cycleLabel(v)} · derived · {money(v.utilization.pricePerSeat ?? 0)}/seat
                          </div>
                        </>
                      ) : (
                        <>
                          <div>{money(v.expense.amount)}</div>
                          <div className="text-xs text-[var(--color-muted)]">{cycleLabel(v)}</div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {v.expense.type !== 'license' ? (
                        <span className="text-[var(--color-muted)]">-</span>
                      ) : v.utilization == null ? (
                        <span className="text-[var(--color-muted)]">
                          {v.expense.seatsPurchased != null ? `${v.expense.seatsPurchased} purchased` : '-'}
                        </span>
                      ) : v.utilization.basis === 'derived' ? (
                        <div>
                          <div>
                            {v.utilization.inUse} of {v.utilization.purchased} in use
                            {v.utilization.seatsAvailable > 0 ? ` (${v.utilization.seatsAvailable} available)` : ''}
                          </div>
                          <div className="text-xs text-[var(--color-muted)]">
                            {(v.utilization.seatsPerBlock ?? 1) > 1 ? `blocks of ${v.utilization.seatsPerBlock}` : 'per-seat'}
                            {v.expense.seatsFloor != null ? ` · floor ${v.expense.seatsFloor}` : ''}
                            {v.utilization.suspendedSeats > 0 ? ` · ${v.utilization.suspendedSeats} suspended` : ''}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className={over ? 'font-medium text-[var(--tint-warn-text)]' : ''}>
                            {v.utilization.inUse} of {v.utilization.purchased} in use
                          </div>
                          {v.utilization.suspendedSeats > 0 ? (
                            <div className="text-xs text-[var(--color-muted)]">
                              {v.utilization.suspendedSeats} suspended
                            </div>
                          ) : null}
                          {over ? <div className="text-xs text-[var(--tint-warn-text)]">over purchased count · legacy entry; edit to move it to per-seat tracking</div> : null}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-[var(--color-muted)]">
                      <div>Day {v.expense.chargeDay}</div>
                      <div className="text-xs">
                        {v.expense.startDate}
                        {v.expense.endDate ? ` → ${v.expense.endDate}` : ' → ongoing'}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex justify-end gap-2">
                        <PillButton onClick={() => toggleAttach(v.expense.id)}>
                          Files{(v.expense.attachments ?? []).length > 0 ? ` (${(v.expense.attachments ?? []).length})` : ''}
                        </PillButton>
                        <PillButton onClick={() => openEdit(v)}>
                          Edit
                        </PillButton>
                        <PillButton tone="red" onClick={() => setDeleteTarget(v)}>
                          Delete
                        </PillButton>
                      </div>
                    </td>
                  </tr>
                  {attachOpen.has(v.expense.id) ? (
                    <tr className="border-t border-[rgb(var(--edge-rgb)/0.05)] bg-[rgb(var(--edge-rgb)/0.02)]">
                      <td colSpan={6} className="px-3 py-3">{renderRecurringAttachments(v)}</td>
                    </tr>
                  ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete this recurring expense?"
        confirmLabel="Delete permanently"
        cancelLabel="Keep it"
        confirmVariant="danger"
        busy={deleteBusy}
        onConfirm={() => void doDelete()}
        onCancel={() => setDeleteTarget(null)}
      >
        <p>
          Permanently delete <strong>{deleteTarget?.expense.description}</strong>? This cannot be
          undone. Every past month this expense would have counted toward disappears from
          profitability and reports with it. If it simply stopped, set an <strong>end date</strong>{' '}
          instead so history stays accurate.
        </p>
      </ConfirmDialog>
    </div>
  );
}


/** The derived-amount line a license form shows in place of an Amount field (nothing to type —
 *  seats × price per seat, live). Uses the saved utilization when editing; previews from the form
 *  inputs when creating. */
function DerivedAmountNote({ form, view }: { form: FormState; view: RecurringExpenseView | null }): JSX.Element {
  const u = view?.utilization;
  if (u && u.basis === 'derived' && u.amountPerCycle != null) {
    // Break in-use into its two sources so a double-count is obvious: portal assignments (counted
    // automatically) vs the "extra seats not in the portal" offset you typed.
    const extra = view?.expense.externalSeats ?? 0;
    const portal = u.inUse - extra;
    return (
      <p className="rounded-md border border-[rgb(var(--edge-rgb)/0.10)] bg-[rgb(var(--edge-rgb)/0.02)] px-3 py-2 text-sm">
        Amount per cycle (derived): <strong>${u.amountPerCycle.toFixed(2)}</strong>.{' '}
        {portal} assigned in the portal{extra > 0 ? <> plus {extra} extra (not in the portal)</> : null},{' '}
        so <strong>{u.inUse} in use</strong> → {u.purchased} purchased × ${(u.pricePerSeat ?? 0).toFixed(2)}/seat.
        {extra > 0 ? (
          <> Those {extra} extra seats come from the “extra seats not assigned in the portal” field; if your seats are all assigned here, set that field to 0.</>
        ) : (
          <> It updates itself as assignments change.</>
        )}
      </p>
    );
  }
  const price = Number(form.pricePerSeat.trim());
  const block = Number(form.seatsPerBlock.trim() === '' ? '1' : form.seatsPerBlock.trim());
  const floor = form.seatsFloor.trim() === '' ? 0 : Number(form.seatsFloor.trim());
  const external = form.externalSeats.trim() === '' ? 0 : Number(form.externalSeats.trim());
  if (!(price >= 0) || !Number.isInteger(block) || block < 1) {
    return (
      <p className="text-xs text-[var(--color-muted)]">
        No amount to type for a license; it derives from seats in use × price per seat once saved.
      </p>
    );
  }
  const ceil = (n: number, b: number): number => Math.ceil(Math.max(0, n) / b) * b;
  const startPurchased = Math.max(ceil(external, block), ceil(Number.isFinite(floor) ? floor : 0, block));
  return (
    <p className="text-xs text-[var(--color-muted)]">
      No amount to type; it derives live. Starting point with {external} seat{external === 1 ? '' : 's'} outside
      the portal{floor > 0 ? ` and a floor of ${floor}` : ''}: {startPurchased} purchased ≈ ${(startPurchased * price).toFixed(2)} per
      cycle; portal assignments add from there.
    </p>
  );
}

/** Service⇄expense COST reconciliation (slice 2, 08 seat-drift resolution — explicit one-click,
 *  NEVER silent: the catalog's Cost to you feeds per-service margin, so a change deserves eyes).
 *  Shows only when this license links a service and the two figures disagree. */
function CostDriftPanel({
  form,
  view,
  onUsePrice,
  onPushed,
}: {
  form: FormState;
  view: RecurringExpenseView | null;
  onUsePrice: (price: string) => void;
  onPushed: () => void;
}): JSX.Element | null {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  if (!view || form.type !== 'license' || !view.service) return null;
  const price = Number(form.pricePerSeat.trim());
  if (!(price >= 0) || form.pricePerSeat.trim() === '') return null;
  const catalog = view.service.defaultCostToYou;
  if (Math.abs(catalog - price) < 0.005) return null;

  const pushToService = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await api.admin.updateService(view.service!.id, { defaultCostToYou: price });
      setDone(`Service cost set to $${price.toFixed(2)}/seat.`);
      onPushed();
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[var(--tint-warn-border)] bg-[var(--tint-warn-bg-soft)] px-3 py-2 text-sm text-[var(--tint-warn-text)] space-y-2">
      {done ? (
        <p>{done}</p>
      ) : (
        <>
          <p>
            <strong>Cost drift:</strong> the service catalog lists <strong>${catalog.toFixed(2)}</strong>/seat
            as your cost for {view.service.name}; this license says <strong>${price.toFixed(2)}</strong>/seat.
            Nothing reconciles silently. Pick a side:
          </p>
          <div className="flex flex-wrap gap-2">
            <PillButton tone="amber" disabled={busy} onClick={() => void pushToService()}>
              {busy ? 'Updating…' : `Set service cost to $${price.toFixed(2)}/seat`}
            </PillButton>
            <PillButton tone="amber" disabled={busy} onClick={() => onUsePrice(String(catalog))}>
              Use the service cost (${catalog.toFixed(2)}) here
            </PillButton>
          </div>
          <p className="text-xs">
            Setting the service cost changes per-service margin everywhere it's shown (recorded in
            the catalog's config history). Using it here just fills the field; Save records the
            price step.
          </p>
        </>
      )}
      {err ? <p className="text-xs text-[var(--tint-red-text)]">{err}</p> : null}
    </div>
  );
}

function Input({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}): JSX.Element {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    />
  );
}

function DateInput({ value, onChange }: { value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <input
      type="date"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    />
  );
}

function Select({
  value,
  onChange,
  children,
}: {
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-[rgb(var(--edge-rgb)/0.10)] px-3 py-2 text-sm bg-[var(--surface-solid)]"
    >
      {children}
    </select>
  );
}
